import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../api";
import CopilotPanel from "../assistant/CopilotPanel";
import CurveChart from "../charts/CurveChart";
import { ErrorBanner, Pill, Spinner } from "../components/common";
import { CONDITION_SHORT, styleFor } from "../lib/conditions";
import { useCurationState } from "../lib/curationState";
import { delta, percent, sig } from "../lib/format";
import { getUser } from "../lib/user";
import { buildViews, conditionsOf, outlierCount } from "../lib/views";

const COMMON_REASONS = [
  "Visible interference in the peak",
  "Internal standard outside tolerance",
  "Integration error",
  "Pipetting error",
  "Sample handling error",
];

const CLASSIFICATION_KIND = {
  "in range": "ok",
  "> max tested": "warning",
  "< min tested": "warning",
  "no measurable inhibition": "neutral",
  "fit failed": "error",
};

/** One condition's fit parameters, and what the last curation change did to the IC50. */
function FitCard({ series, previousIc50, showCompound }) {
  const { fit, condition } = series;
  const style = styleFor(condition);
  const change = fit && previousIc50 !== undefined ? delta(previousIc50, fit.ic50_um) : null;

  return (
    <div className="fit-card" style={{ borderLeftColor: style.color }}>
      <div className="muted">
        {CONDITION_SHORT[condition] || condition}
        {/* On the standards view each condition is a different compound, and
            which one it is matters more than the condition label alone. */}
        {showCompound && <span className="fit-card-compound">{series.compound}</span>}
      </div>
      {fit ? (
        <>
          <div className="ic50">
            {fit.ic50_display}
            {/* A verdict such as "no measurable inhibition" is not a quantity. */}
            {/^[\d<>]/.test(fit.ic50_display) && <span className="ic50-unit"> µM</span>}
          </div>
          <div style={{ marginTop: "0.15rem" }}>
            <Pill kind={CLASSIFICATION_KIND[fit.classification] || "neutral"}>
              {fit.classification}
            </Pill>
            {change && (
              <span className="muted" style={{ marginLeft: "0.4rem", fontSize: "0.8rem" }}>
                {change} vs previous fit
              </span>
            )}
          </div>
          <dl>
            <dt>95 % CI</dt>
            <dd>
              {fit.ic50_ci_low ? `${sig(fit.ic50_ci_low, 3)} – ${sig(fit.ic50_ci_high, 3)}` : "—"}
            </dd>
            <dt>Hill slope</dt>
            <dd>{sig(fit.hill_slope, 3)}</dd>
            <dt>R²</dt>
            <dd>{sig(fit.r_squared, 3)}</dd>
            <dt>Residual SE</dt>
            <dd>{sig(fit.residual_se, 3)}</dd>
            <dt>n (outliers)</dt>
            <dd>
              {fit.n_observations} ({fit.n_excluded})
            </dd>
          </dl>
        </>
      ) : (
        <div className="muted" style={{ marginTop: "0.4rem" }}>
          Not fitted.
        </div>
      )}
    </div>
  );
}

/**
 * One Download button; the choice of artefact is inside it.
 *
 * Both artefacts are generated from the run's current curation at the moment the
 * link is followed, so a download can never be one edit behind what is on
 * screen. The workbook carries the figures too, and is the whole report; the zip
 * is for dropping a single curve into a slide.
 */
function DownloadMenu({ runId }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!box.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="menu" ref={box}>
      <button
        type="button"
        className="primary"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        Download <span className="menu-caret">▾</span>
      </button>
      {open && (
        <div className="menu-panel" role="menu">
          <a
            role="menuitem"
            href={api.exportUrl(runId, "workbook")}
            onClick={() => setOpen(false)}
          >
            <strong>Workbook (.xlsx)</strong>
            <span className="muted">Every table, the QC record, the audit trail and the curves</span>
          </a>
          <a
            role="menuitem"
            href={api.exportUrl(runId, "plots", "?formats=png")}
            onClick={() => setOpen(false)}
          >
            <strong>Curves (.png)</strong>
            <span className="muted">One 300 dpi figure per curve, zipped</span>
          </a>
        </div>
      )}
    </div>
  );
}

/**
 * Step 3, and the heart of the application: the data values and the curve side
 * by side.
 *
 * A point marked as an outlier is never deleted -- it stays in the table struck
 * through, with its reason, and can be unmarked. Every change re-fits on the
 * server and the new IC50 is shown against the previous one, so the effect of
 * removing a point is visible rather than assumed.
 *
 * The table shows one condition at a time. All three are on the curve together,
 * as they are in the exported figure, but 36 rows per condition stacked into one
 * table is a scrolling exercise rather than a reading one.
 */
export default function CurateScreen() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [compounds, setCompounds] = useState(null);
  const [reason, setReason] = useState(COMMON_REASONS[0]);
  const [previousIc50, setPreviousIc50] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // The six values the screen draws itself from. They live in a hook rather
  // than here so the copilot can move them through a validated intent, which is
  // the only way anything other than a click reaches them. `showOutliers` is
  // off by default: outliers are not in the fit, and a curve read against
  // points it never saw is a curve read wrongly.
  const {
    selectedView,
    activeCondition,
    selected,
    highlighted,
    showOutliers,
    hiddenConditions,
    pendingScreen,
    setActiveCondition,
    setSelected,
    setHighlighted,
    setShowOutliers,
    setHiddenConditions,
    openView: openViewKey,
    apply,
    clearPendingScreen,
  } = useCurationState();

  const load = useCallback(
    async (keepPrevious) => {
      const data = await api.getCompounds(runId);
      setCompounds(data.compounds);
      if (!keepPrevious) setPreviousIc50({});
      return data.compounds;
    },
    [runId]
  );

  useEffect(() => {
    let live = true;
    api
      .getCompounds(runId)
      .then((data) => live && setCompounds(data.compounds))
      .catch((e) => live && setError(e));
    return () => {
      live = false;
    };
  }, [runId]);

  // A `navigate` intent is the one thing the reducer cannot do itself.
  useEffect(() => {
    if (!pendingScreen) return;
    clearPendingScreen();
    if (pendingScreen !== "curate") navigate(`/runs/${runId}/${pendingScreen}`);
  }, [pendingScreen, clearPendingScreen, navigate, runId]);

  const views = useMemo(() => (compounds ? buildViews(compounds) : []), [compounds]);

  // The standards view leads, so it is what opens.
  const current = useMemo(
    () => views.find((v) => v.key === selectedView) || views[0] || null,
    [views, selectedView]
  );

  const tabs = useMemo(() => (current ? conditionsOf(current) : []), [current]);

  const totalOutliers = useMemo(
    () => views.reduce((total, view) => total + outlierCount(view), 0),
    [views]
  );

  const condition = tabs.includes(activeCondition) ? activeCondition : tabs[0] || null;

  // A figure with nothing on it is not a state worth being able to reach, so a
  // set of hidden conditions carried over from another view that would empty
  // this one is read as none hidden.
  const hidden = tabs.length && tabs.every((c) => hiddenConditions.includes(c)) ? [] : hiddenConditions;
  const visibleConditions = tabs.filter((c) => !hidden.includes(c)).length;

  function toggleCondition(c) {
    setHiddenConditions((was) =>
      hidden.includes(c) ? was.filter((x) => x !== c) : [...hidden, c]
    );
  }

  /** Rows for the open tab only: highest concentration first, replicates together. */
  const rows = useMemo(() => {
    if (!current) return [];
    return current.series
      .filter((s) => s.condition === condition)
      .flatMap((s) => s.observations)
      .sort(
        (a, b) =>
          (b.concentration_um ?? 0) - (a.concentration_um ?? 0) ||
          (a.series || "").localeCompare(b.series || "")
      );
  }, [current, condition]);

  /** Every point in the view, so a click on the curve can be resolved to its tab. */
  const byId = useMemo(() => {
    const map = new Map();
    current?.series.forEach((s) => s.observations.forEach((o) => map.set(o.observation_id, o)));
    return map;
  }, [current]);

  const selectedIncluded = selected.filter((id) => byId.get(id)?.included);
  const selectedExcluded = selected.filter((id) => byId.get(id) && !byId.get(id).included);

  function toggle(observationId) {
    // A point clicked on the curve may belong to a condition the table is not
    // showing; follow it, so the selection is always visible in the table.
    const point = byId.get(observationId);
    if (point && point.condition !== condition) setActiveCondition(point.condition);
    setSelected((current) =>
      current.includes(observationId)
        ? current.filter((id) => id !== observationId)
        : [...current, observationId]
    );
  }

  function openView(view) {
    openViewKey(view.key);
    setPreviousIc50({});
  }

  /** Each series' IC50 as it stands, so the next fit can be compared with it. */
  function snapshot() {
    return Object.fromEntries((current?.series || []).map((s) => [s.key, s.fit?.ic50_um ?? null]));
  }

  async function run(action) {
    setBusy(true);
    setError(null);
    const before = snapshot();
    try {
      await action();
      await load(true);
      setPreviousIc50(before);
      setSelected([]);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  /**
   * A confirmed copilot proposal has already gone through the ordinary curation
   * route by the time this runs, so the screen just re-reads the fits. No "vs
   * previous fit" delta: the comparison needs a snapshot taken before the
   * change, and there was no click here to take one at.
   */
  const onCurationChanged = useCallback(async () => {
    setPreviousIc50({});
    await load(true);
    setSelected([]);
  }, [load, setSelected]);

  const markOutliers = () =>
    run(() => api.exclude(runId, selectedIncluded, reason.trim(), getUser() || null));

  const unmark = () =>
    run(async () => {
      for (const id of selectedExcluded) {
        await api.include(runId, id, getUser() || null);
      }
    });

  if (error && !compounds) return <ErrorBanner error={error} />;
  if (!compounds) return <Spinner label="Fitting curves…" />;

  return (
    <>
      <ErrorBanner error={error} onDismiss={() => setError(null)} />
      <div className="toolbar">
        <span className="muted">
          {totalOutliers === 0
            ? "No points marked as outliers."
            : `${totalOutliers} point${totalOutliers === 1 ? "" : "s"} marked as outliers.`}{" "}
          Exports follow the curation as it stands.
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: "0.75rem", alignItems: "center" }}>
          {/* The way into exploratory analysis, and the only one. It sits beside
              the download rather than in the step nav: what it produces is not
              part of this run's result and cannot be exported. */}
          <Link className="explore-link" to={`/runs/${runId}/explore`}>
            Open exploration
          </Link>
          <DownloadMenu runId={runId} />
        </div>
      </div>
      <div className="curate-layout">
        <div className="card">
          <h3>Curves</h3>
          <ul className="compound-list">
            {views.map((view) => {
              const outliers = outlierCount(view);
              return (
                <li key={view.key}>
                  <button
                    className={view.key === current?.key ? "selected" : ""}
                    onClick={() => openView(view)}
                  >
                    {view.name}
                    <span className="sub">
                      {view.kind}
                      {outliers > 0 && ` · ${outliers} outlier${outliers === 1 ? "" : "s"}`}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div>
          {current && (
            <>
              <div className="card">
                <h2>{current.name}</h2>
                <p className="card-note">{current.note}</p>
                <div className="fit-grid">
                  {current.series.map((series) => (
                    <FitCard
                      key={series.key}
                      series={series}
                      previousIc50={previousIc50[series.key]}
                      showCompound={current.showCompound}
                    />
                  ))}
                </div>
              </div>

              <div className="split">
                <div className="card">
                  <h3>Observations</h3>
                  <p className="card-note">
                    Click a row or a point to select it. Selected points are ringed on the curve.
                    Marking a point as an outlier re-fits that curve straight away and takes the
                    point off the figure; it stays here, flagged, and can be unmarked.
                  </p>

                  <div className="tabs" role="tablist">
                    {tabs.map((c) => {
                      const series = current.series.filter((s) => s.condition === c);
                      const outliers = series.reduce(
                        (total, s) => total + s.observations.filter((o) => !o.included).length,
                        0
                      );
                      return (
                        <button
                          key={c}
                          role="tab"
                          aria-selected={c === condition}
                          className={c === condition ? "tab selected" : "tab"}
                          style={c === condition ? { borderBottomColor: styleFor(c).color } : null}
                          onClick={() => setActiveCondition(c)}
                        >
                          {CONDITION_SHORT[c] || c}
                          {outliers > 0 && <span className="tab-badge">{outliers}</span>}
                        </button>
                      );
                    })}
                  </div>

                  <div className="table-scroll">
                    <table>
                      <thead>
                        <tr>
                          {current.showCompound && <th>Compound</th>}
                          <th>Series</th>
                          <th className="num">Konz</th>
                          <th className="num unit">µM</th>
                          <th className="num unit">ng/mL</th>
                          <th className="num">% control</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((o) => {
                          const classes = [];
                          if (!o.included) classes.push("excluded");
                          if (o.observation_id === highlighted) classes.push("highlighted");
                          return (
                            <tr
                              key={o.observation_id}
                              className={classes.join(" ")}
                              style={{
                                cursor: "pointer",
                                outline: selected.includes(o.observation_id)
                                  ? "2px solid var(--brand)"
                                  : "none",
                                outlineOffset: "-2px",
                              }}
                              onMouseEnter={() => setHighlighted(o.observation_id)}
                              onMouseLeave={() => setHighlighted(null)}
                              onClick={() => toggle(o.observation_id)}
                            >
                              {current.showCompound && <td>{o.compound}</td>}
                              <td className="mono">{o.series}</td>
                              <td className="num">{o.concentration_point}</td>
                              <td className="num">{sig(o.concentration_um, 3)}</td>
                              <td className="num">{sig(o.response, 4)}</td>
                              <td className="num">{percent(o.percent_of_control)}</td>
                              <td
                                className="muted"
                                style={{ whiteSpace: "normal", maxWidth: 180 }}
                              >
                                {/* The tag, not the strikethrough, is what says
                                    "outlier": struck-through text alone reads as
                                    a styling accident at a glance. */}
                                {!o.included && <span className="outlier-tag">outlier</span>}
                                {o.exclusion_reason || ""}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="field" style={{ marginTop: "1rem" }}>
                    <label htmlFor="reason">Reason for marking as outlier</label>
                    <input
                      id="reason"
                      type="text"
                      list="common-reasons"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                    <datalist id="common-reasons">
                      {COMMON_REASONS.map((r) => (
                        <option key={r} value={r} />
                      ))}
                    </datalist>
                  </div>

                  <div className="button-row">
                    <button
                      className="primary"
                      disabled={busy || !selectedIncluded.length || !reason.trim()}
                      onClick={markOutliers}
                    >
                      Mark {selectedIncluded.length || ""} point
                      {selectedIncluded.length === 1 ? "" : "s"} as outlier
                    </button>
                    <button disabled={busy || !selectedExcluded.length} onClick={unmark}>
                      Unmark {selectedExcluded.length || ""} point
                      {selectedExcluded.length === 1 ? "" : "s"}
                    </button>
                    {selected.length > 0 && !busy && (
                      <button className="link" onClick={() => setSelected([])}>
                        clear selection
                      </button>
                    )}
                    {busy && <Spinner label="Re-fitting…" />}
                  </div>
                </div>

                <div className="card">
                  <div className="chart-controls">
                    {tabs.map((c) => {
                      const on = !hidden.includes(c);
                      return (
                        <button
                          key={c}
                          type="button"
                          className={on ? "chip" : "chip off"}
                          style={on ? { borderColor: styleFor(c).color } : null}
                          aria-pressed={on}
                          // The last visible condition cannot be hidden -- see above.
                          disabled={on && visibleConditions === 1}
                          title={on ? `Hide ${c}` : `Show ${c}`}
                          onClick={() => toggleCondition(c)}
                        >
                          <span
                            className="chip-dot"
                            style={{ background: on ? styleFor(c).color : "transparent" }}
                          />
                          {CONDITION_SHORT[c] || c}
                        </button>
                      );
                    })}
                    <label className="chip-check" style={{ marginLeft: "auto" }}>
                      <input
                        type="checkbox"
                        checked={showOutliers}
                        disabled={outlierCount(current) === 0}
                        onChange={(e) => setShowOutliers(e.target.checked)}
                      />
                      Show outliers
                      {outlierCount(current) > 0 && ` (${outlierCount(current)})`}
                    </label>
                  </div>
                  <CurveChart
                    series={current.series}
                    title={current.figureTitle}
                    highlighted={highlighted}
                    selected={selected}
                    showOutliers={showOutliers}
                    hiddenConditions={hidden}
                    onPointClick={toggle}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <CopilotPanel runId={runId} apply={apply} onCurationChanged={onCurationChanged} />
    </>
  );
}
